import * as React from 'react';
import { TextAction } from '@leagueplatform/genesis-core';
import { NavLink, useLocation } from 'react-router-dom';
import type { UseTreeItem2Parameters } from '@mui/x-tree-view/useTreeItem2';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';
import type { TreeNodeType } from '@web-config-app/core';

/**
 * See: customizing TreeItem using TreeItem2
 * https://mui.com/x/api/tree-view/tree-item-2/
 * https://mui.com/x/react-tree-view/simple-tree-view/customization/#custom-label
 */

interface EntityTreeItemProps
  extends Omit<UseTreeItem2Parameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {
  onClick?: (args: any) => void;
  path: string;
  type: TreeNodeType | undefined;
  index?: number;
  isSidePanelExpanded?: boolean;
}

type EntityTreeItemLabelProps = {
  to: string;
  onClick?: (args: any) => void;
  label: string;
  type: TreeNodeType | undefined;
  locationState?: any;
  isSidePanelExpanded?: boolean;
};

const EntityTreeItemLabel = ({
  to,
  onClick,
  label,
  type,
  locationState,
  isSidePanelExpanded = false,
}: EntityTreeItemLabelProps) => (
  <TextAction
    as={NavLink}
    to={to}
    state={locationState}
    css={{
      flexGrow: 1,
      display: 'flex',
      // Align the counter circle to the top of the first line rather than
      // vertically centered across all lines, so wrapped labels look right.
      alignItems: 'flex-start',
      // Wrap only when the side panel is expanded. When collapsed, labels
      // stay on one line and overflow horizontally so the nav scrollbar
      // lets the user scroll to read long labels.
      whiteSpace: isSidePanelExpanded ? 'normal' : 'nowrap',
      wordBreak: 'break-word',
      typography: '$subtitleOne',
      color: '$onSurfaceTextPrimary',
      '&.CACT-tree-item-arrayItem:before': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        typography: '$label',
        height: '$one',
        width: '$one',
        // Nudge down so the circle visually centers with the first line
        // of subtitleOne text rather than aligning to its very top.
        marginBlockStart: '$quarter',
        marginInlineEnd: '$half',
        backgroundColor: '$surfaceBackgroundTertiary',
        color: '$onSurfaceTextSubdued',
        borderRadius: '$circle',
        counterIncrement: 'array-item-count',
        content: 'counter(array-item-count)',
      },
    }}
    className={`CACT-tree-item-${type}`}
    onClick={onClick}
  >
    {label}
  </TextAction>
);

export const EntityTreeItem = React.forwardRef(
  (props: EntityTreeItemProps, ref: React.Ref<HTMLLIElement>) => {
    const { path, onClick, label, type, index, isSidePanelExpanded } = props;
    const { pathname, state: locationState } = useLocation();

    return (
      <TreeItem2
        {...props}
        ref={ref}
        slots={{
          label: EntityTreeItemLabel,
        }}
        slotProps={{
          label: {
            label,
            to: `${pathname}?path=${path}`,
            onClick,
            type,
            index,
            locationState,
            isSidePanelExpanded,
          } as any,
        }}
      />
    );
  },
);
